import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faPause, faPlay, faPhone, faMobileAlt } from '@fortawesome/free-solid-svg-icons';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import { checkWeightValue, dedupeMeasurementsByDateTimeKeepLatest, getMeasurementValueClass, getPresetDateRangeParams, formatDateDisplay } from '../../../utils/measurementUtils';
import measurementService from '../../../services/measurementService';
import patientService from '../../../services/patientService';
import { toast } from 'react-toastify';
import AddWForm from './AddWForm';
import AddFollowUpModal from '../../../components/AddFollowUpModal/AddFollowUpModal';
import VitalsTabs from '../../../components/MeasurementData/VitalsTabs/VitalsTabs';
import EmailModal from '../../../components/EmailModal/EmailModal';
import emailService from '../../../services/emailService';
import { printElement } from '../../../utils/reportExportUtils';
import { exportWeightTablesPdf } from '../../../utils/measurementTablePdfExport';
import {
  findPeriodByName,
  normalizePeriodName,
  resolvePeriodNameByIdOrLabel,
  resolveDisplayScheduleTargets,
  hasAnyScheduleTargets,
  getMeasurementEpochMs
} from '../../../utils/scheduleTargetUtils';
import {
  PATIENT_SCHEDULE_TARGET_PREF_EVENT,
  getUsePatientScheduleTargetsAtMeasurementMs,
  getUsePatientScheduleTargetsPreference
} from '../../../utils/patientScheduleTargetPreference.js';
import PageLoadingSpinner from '../../../components/PageLoadingSpinner/PageLoadingSpinner';
import './Weight.css';

const Weight = ({ practiceId, patientId, practiceIsLocked = false }) => {
  const WEIGHT_PERMANENT_START_TIMES = {
    Morning: '06:00 AM',
    Afternoon: '12:00 PM',
    Evening: '06:00 PM'
  };
  const { updateBreadcrumbs } = useBreadcrumb();
  const [timer, setTimer] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [queryType, setQueryType] = useState('dateRange');
  const [dateRange, setDateRange] = useState('Last 30 days');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [sortBy, setSortBy] = useState('Weekly average');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [patientData, setPatientData] = useState(null);
  const [measurements, setMeasurements] = useState([]);
  const [scheduleTargets, setScheduleTargets] = useState(null);
  const [scheduleTargetSources, setScheduleTargetSources] = useState(null);
  const [targetTimelineRev, setTargetTimelineRev] = useState(0);
  const [loading, setLoading] = useState(false);
  const [queryLoading, setQueryLoading] = useState(false);
  const [hasFetchedMeasurements, setHasFetchedMeasurements] = useState(false);
  const [hasFetchedPatientData, setHasFetchedPatientData] = useState(false);
  const [hasFetchedScheduleTargets, setHasFetchedScheduleTargets] = useState(false);
  const patientVitals = patientData?.patient?.vitals || 'BP, BG, WT';
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const initialized = useRef(false);
  const queryStateBeforeAddRef = useRef(null);

  useEffect(() => {
    if (practiceIsLocked) {
      setShowAddModal(false);
      setShowFollowUpModal(false);
      setIsRunning(false);
      setTimer(0);
    }
  }, [practiceIsLocked]);

  // Fetch patient data and schedule targets on mount
  useEffect(() => {
    if (practiceId && patientId) {
      setHasFetchedPatientData(false);
      setHasFetchedScheduleTargets(false);
      setHasFetchedMeasurements(false);
      setScheduleTargetSources(null);
      if (!initialized.current) {
        if (!practiceIsLocked) {
          setIsRunning(true);
        }
        initialized.current = true;
      }
      fetchPatientData();
      fetchScheduleTargets();
    }
    return () => {
      initialized.current = false;
    };
  }, [practiceId, patientId]);

  // Fetch measurements only on patient/screen load.
  // Query filters are applied explicitly via the Query button.
  useEffect(() => {
    if (practiceId && patientId) {
      fetchMeasurements({ silent: true });
    }
  }, [practiceId, patientId]);

  // Timer effect
  useEffect(() => {
    let interval = null;
    if (isRunning) {
      interval = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRunning]);

  const formatTimer = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const fetchPatientData = async () => {
    const cacheKey = `measurement:patient:${practiceId}:${patientId}`;
    const cachedRaw = sessionStorage.getItem(cacheKey);
    if (cachedRaw) {
      try {
        const cached = JSON.parse(cachedRaw);
        if (cached && typeof cached === 'object') setPatientData(cached);
      } catch {
        // ignore
      }
    }
    try {
      const response = await patientService.getMeasurementHeaderFast(practiceId, patientId);
      if (response.data.success) {
        const next = response.data.data;
        setPatientData(next);
        sessionStorage.setItem(cacheKey, JSON.stringify(next));
      }
    } catch (error) {
      console.error('Error fetching patient data:', error);
    } finally {
      setHasFetchedPatientData(true);
    }
  };

  const fetchScheduleTargets = async () => {
    try {
      const sources = await patientService.getScheduleTargetSourcesForPatient(practiceId, patientId);
      setScheduleTargetSources(sources);
      const usePatientTargets = getUsePatientScheduleTargetsPreference(practiceId, patientId, sources?.patientData);
      const effective = resolveDisplayScheduleTargets({
        practiceData: sources.practiceData,
        patientData: sources.patientData,
        usePatientTargets
      });
      setScheduleTargets(hasAnyScheduleTargets(effective) ? effective : sources.practiceData);
    } catch (error) {
      console.error('Error fetching schedule targets:', error);
    } finally {
      setHasFetchedScheduleTargets(true);
    }
  };

  useEffect(() => {
    if (!practiceId || !patientId) return undefined;
    const onPref = (ev) => {
      const d = ev?.detail;
      if (String(d?.practiceId) !== String(practiceId) || String(d?.patientId) !== String(patientId)) return;
      setTargetTimelineRev((n) => n + 1);
      fetchScheduleTargets();
    };
    window.addEventListener(PATIENT_SCHEDULE_TARGET_PREF_EVENT, onPref);
    return () => window.removeEventListener(PATIENT_SCHEDULE_TARGET_PREF_EVENT, onPref);
  }, [practiceId, patientId]);

  const fetchMeasurements = async ({ silent = false, bypassCache = false } = {}) => {
    try {
      let fromDateParam, toDateParam;

      if (queryType === 'dateRange') {
        const params = getPresetDateRangeParams(dateRange);
        fromDateParam = params.fromDateParam;
        toDateParam = params.toDateParam;
      } else {
        // Inputs are already YYYY-MM-DD from type="date"
        if (fromDate && toDate) {
          fromDateParam = fromDate;
          toDateParam = toDate;
        }
      }

      if (fromDateParam && toDateParam) {
        const queryCacheKey = `measurement:wt:${practiceId}:${patientId}:${queryType}:${dateRange}:${fromDateParam}:${toDateParam}:${sortBy}`;
        let hadCachedData = false;
        if (!bypassCache) {
          const cachedRaw = sessionStorage.getItem(queryCacheKey);
          if (cachedRaw) {
            try {
              const cached = JSON.parse(cachedRaw);
              if (Array.isArray(cached)) {
                setMeasurements(cached);
                setHasFetchedMeasurements(true);
                hadCachedData = true;
              }
            } catch {
              // ignore
            }
          }
        }
        if (!silent || !hadCachedData) {
          setQueryLoading(true);
        }
        const sortKeyMap = {
          '1 Day Average': 'daily_average',
          'Weekly average': 'weekly_average',
          '14 Days Average': 'weekly_average',
          'Monthly average': 'monthly_average',
          '3 Months Average': 'monthly_average',
          'All': 'date',
          'Daily average': 'daily_average'
        };
        const response = await measurementService.getWeightByDateRange(
          practiceId,
          patientId,
          fromDateParam,
          toDateParam,
          sortKeyMap[sortBy] || 'weekly_average'
        );

        if (response.data?.success) {
          const next = response.data.data || [];
          setMeasurements((prev) => (JSON.stringify(prev || []) === JSON.stringify(next) ? prev : next));
          sessionStorage.setItem(queryCacheKey, JSON.stringify(next));
          setLoading(false);
        }
      }
    } catch (error) {
      console.error('Error fetching measurements:', error);
      if (error.response?.status === 404) {
        console.log('Measurement API endpoint not yet available');
        setMeasurements([]);
      } else {
        toast.error('Failed to load measurements');
        setMeasurements([]);
      }
    } finally {
      setQueryLoading(false);
      setLoading(false);
      setHasFetchedMeasurements(true);
    }
  };

  const handleQuery = () => {
    if (queryType === 'from' && (!fromDate || !toDate)) {
      toast.warning('Please select both start and end dates');
      return;
    }
    
    fetchMeasurements({ silent: false, bypassCache: true });
  };

  const handleAddDataSuccess = (newReading = null) => {
    const prevQuery = queryStateBeforeAddRef.current;
    if (prevQuery) {
      setQueryType(prevQuery.queryType);
      setDateRange(prevQuery.dateRange);
      setFromDate(prevQuery.fromDate);
      setToDate(prevQuery.toDate);
    }

    try {
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith(`measurement:wt:${practiceId}:${patientId}`)) {
          sessionStorage.removeItem(key);
        }
      });
    } catch {
      // ignore
    }

    const selectedDate = newReading?.measurement_date;
    let shouldInsertOptimistically = false;
    if (selectedDate) {
      const selected = new Date(selectedDate);
      selected.setHours(0, 0, 0, 0);
      if (queryType === 'from' && fromDate && toDate) {
        const from = new Date(fromDate);
        const to = new Date(toDate);
        from.setHours(0, 0, 0, 0);
        to.setHours(0, 0, 0, 0);
        shouldInsertOptimistically = selected >= from && selected <= to;
      } else {
        const days = parseInt(dateRange.replace(/\D/g, '')) || 30;
        const end = new Date();
        end.setHours(0, 0, 0, 0);
        const start = new Date(end);
        start.setDate(start.getDate() - days);
        shouldInsertOptimistically = selected >= start && selected <= end;
      }
    }

    if (newReading && selectedDate && newReading.measurement_time && shouldInsertOptimistically) {
      const timeFull = `${newReading.measurement_time}:00`;
      const weightKg = Number(newReading.weight);
      const optimisticMeasurement = {
        id: newReading.id || `local-${selectedDate}-${newReading.measurement_time}-${Date.now()}`,
        weight: Number.isFinite(weightKg) ? weightKg : null,
        fat: newReading.body_fat ?? null,
        body_fat: newReading.body_fat ?? null,
        bmi: newReading.bmi ?? null,
        period_name: newReading.period_name || null,
        note: newReading.note || null,
        measurement_date: selectedDate,
        measurement_time: newReading.measurement_time,
        measure_date_time: `${selectedDate} ${timeFull}`,
        measure_new_date_time: `${selectedDate}T${timeFull}`
      };
      setMeasurements((prev) => [optimisticMeasurement, ...(prev || [])]);
    }

    // Keep current query period/date filters unchanged; refresh in background without cache.
    fetchMeasurements({ silent: false, bypassCache: true });
  };

  const handleOpenAddModal = () => {
    queryStateBeforeAddRef.current = {
      queryType,
      dateRange,
      fromDate,
      toDate
    };
    setShowAddModal(true);
  };

  const formatDate = (dateStr) => formatDateDisplay(dateStr);

  const formatDateIso = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    if (timeString.includes('AM') || timeString.includes('PM')) {
      return timeString;
    }
    const parts = timeString.split(':');
    const h = parseInt(parts[0]);
    const m = parts[1] ? parts[1].slice(0, 2) : '00';
    const period = h >= 12 ? 'PM' : 'AM';
    const displayHours = h % 12 || 12;
    return `${displayHours}:${m} ${period}`;
  };

  const resolvePeriodByTime = (timeString, periods) => {
    if (!timeString || !periods?.length) return '';
    let h, m;
    if (timeString.includes('AM') || timeString.includes('PM')) {
      const [time, ampm] = timeString.trim().split(/\s+/);
      const [hh, mm] = time.split(':').map(Number);
      h = hh;
      if (ampm?.toUpperCase() === 'PM' && h !== 12) h += 12;
      if (ampm?.toUpperCase() === 'AM' && h === 12) h = 0;
      m = mm || 0;
    } else {
      const parts = timeString.split(':').map(Number);
      h = parts[0] || 0;
      m = parts[1] || 0;
    }
    const cur = h * 60 + m;
    const convertToMinutes = (t) => {
      if (!t) return 0;
      if (t.includes('AM') || t.includes('PM')) {
        const [time, ampm] = t.trim().split(/\s+/);
        const [hh, mm] = time.split(':').map(Number);
        let hour = hh;
        if (ampm?.toUpperCase() === 'PM' && hour !== 12) hour += 12;
        if (ampm?.toUpperCase() === 'AM' && hour === 12) hour = 0;
        return hour * 60 + (mm || 0);
      }
      const [hh, mm] = t.split(':').map(Number);
      return (hh || 0) * 60 + (mm || 0);
    };
    for (const p of periods) {
      const start = convertToMinutes(p.startTime || p.start);
      let end = convertToMinutes(p.endTime || p.end);
      if (end < start) end += 1440;
      if (cur >= start && cur < end) return p.name;
      if (cur + 1440 >= start && cur + 1440 < end) return p.name;
    }
    return '';
  };

  const resolveTargetsBundleAtMs = useCallback(
    (ms) => {
      if (!scheduleTargetSources) return null;
      const usePatient = getUsePatientScheduleTargetsAtMeasurementMs(
        practiceId,
        patientId,
        ms,
        scheduleTargetSources.patientData
      );
      return resolveDisplayScheduleTargets({
        practiceData: scheduleTargetSources.practiceData,
        patientData: scheduleTargetSources.patientData,
        usePatientTargets: usePatient
      });
    },
    [scheduleTargetSources, practiceId, patientId, targetTimelineRev]
  );

  // Get period names from schedule targets
  const periodNames = useMemo(() => {
    const source = !scheduleTargets?.weightTimePeriod
      ? ['Morning', 'Afternoon', 'Evening']
      : scheduleTargets.weightTimePeriod.map((p) => p?.name).filter(Boolean);
    const seen = new Set();
    return source.filter((name) => {
      const key = normalizePeriodName(name);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [scheduleTargets]);

  const getPeriodColor = () => '#DEF3F5';

  // Get period info (including time ranges) from schedule targets
  const getPeriodInfo = (periodName) => {
    if (!scheduleTargets?.weightTimePeriod) {
      return null;
    }
    
    const period = findPeriodByName(scheduleTargets.weightTimePeriod, periodName);
    
    if (!period) return null;
    
    const fixedStartTime = WEIGHT_PERMANENT_START_TIMES[period.name] || WEIGHT_PERMANENT_START_TIMES[periodName] || '';
    const startTime = fixedStartTime || period.startTime || period.start || '';
    const endTime = period.endTime || period.end || '';
    
    // Format time for display
    const formatTimeDisplay = (time) => {
      if (!time) return '';
      if (time.includes('AM') || time.includes('PM')) return time;
      const [h, m] = time.split(':');
      const hour = parseInt(h);
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${m || '00'} ${period}`;
    };
    
    return {
      name: period.name,
      startTime: startTime,
      endTime: endTime,
      startTimeDisplay: formatTimeDisplay(startTime),
      endTimeDisplay: formatTimeDisplay(endTime)
    };
  };

  // Get target for weight
  const getWeightTarget = (bundleOverride) => {
    const bundle = bundleOverride || scheduleTargets;
    if (!bundle?.weightTargetRange) {
      return { weightMin: 66, weightMax: 220, bodyFatMin: 14, bodyFatMax: 27 };
    }
    return bundle.weightTargetRange;
  };

  const effectiveMeasurements = useMemo(
    () => dedupeMeasurementsByDateTimeKeepLatest(measurements),
    [measurements]
  );

  const awaitingTargetsForColoring = useMemo(
    () =>
      effectiveMeasurements.length > 0 &&
      (!hasFetchedScheduleTargets || scheduleTargetSources == null || !hasFetchedMeasurements),
    [
      effectiveMeasurements.length,
      hasFetchedScheduleTargets,
      scheduleTargetSources,
      hasFetchedMeasurements
    ]
  );

  // Group measurements by date
  const groupedMeasurements = useMemo(() => {
    const grouped = {};
    
    const sortedMeasurements = [...effectiveMeasurements].sort((a, b) => {
      const dateA = a.measurement_date || a.measure_new_date_time?.split('T')[0] || a.created_at?.split('T')[0] || '';
      const dateB = b.measurement_date || b.measure_new_date_time?.split('T')[0] || b.created_at?.split('T')[0] || '';
      
      if (dateA !== dateB) {
        return new Date(dateB) - new Date(dateA);
      }
      
      const timeA = a.measurement_time || 
                   a.measure_new_date_time?.split('T')[1] || 
                   a.measure_date_time?.split(' ')[1] || 
                   a.created_at?.split('T')[1] || 
                   '00:00:00';
      const timeB = b.measurement_time || 
                   b.measure_new_date_time?.split('T')[1] || 
                   b.measure_date_time?.split(' ')[1] || 
                   b.created_at?.split('T')[1] || 
                   '00:00:00';
      return timeB.localeCompare(timeA);
    });

    // Deduplicate exact duplicates only:
    // same date+time + same reading values => keep latest.
    // same date+time + different values => keep both.
    const uniqueByDateTime = new Map();
    sortedMeasurements.forEach((m) => {
      const date = m.measurement_date || m.measure_new_date_time?.split('T')[0] || m.created_at?.split('T')[0] || '';
      const time = m.measurement_time ||
        m.measure_new_date_time?.split('T')[1]?.slice(0, 5) ||
        m.measure_date_time?.split(' ')[1]?.slice(0, 5) || '';
      const key = `${date}|${time}|${m.weight ?? ''}|${m.fat ?? m.body_fat ?? ''}|${m.bmi ?? ''}`;
      if (!uniqueByDateTime.has(key)) {
        uniqueByDateTime.set(key, m);
      }
    });
    
    Array.from(uniqueByDateTime.values()).forEach(measurement => {
      const date = measurement.measurement_date || measurement.measure_new_date_time?.split('T')[0] || measurement.created_at?.split('T')[0];
      
      if (!grouped[date]) {
        grouped[date] = [];
      }
      
      grouped[date].push(measurement);
    });
    
    return grouped;
  }, [effectiveMeasurements]);

  const sortedDates = useMemo(() => {
    return Object.keys(groupedMeasurements).sort((a, b) => new Date(b) - new Date(a));
  }, [groupedMeasurements]);

  const dateBuckets = useMemo(() => {
    if (sortedDates.length === 0) return [];

    if (sortBy === 'All') {
      return [{ label: 'All Dates', dates: sortedDates }];
    }

    const daysPerBucket = {
      '1 Day Average': 1,
      'Weekly average': 7,
      '14 Days Average': 14,
      'Monthly average': 30,
      '3 Months Average': 90,
      'Daily average': 1
    }[sortBy] || 7;

    const newest = new Date(sortedDates[0]);
    newest.setHours(0, 0, 0, 0);
    const msPerDay = 24 * 60 * 60 * 1000;

    const buckets = new Map();
    sortedDates.forEach((dateStr) => {
      const d = new Date(dateStr);
      d.setHours(0, 0, 0, 0);
      const diffDays = Math.floor((newest - d) / msPerDay);
      const idx = Math.floor(diffDays / daysPerBucket);
      if (!buckets.has(idx)) buckets.set(idx, []);
      buckets.get(idx).push(dateStr);
    });

    return Array.from(buckets.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([idx, dates]) => {
        const sorted = [...dates].sort((a, b) => new Date(b) - new Date(a));
        const start = sorted[sorted.length - 1];
        const end = sorted[0];
        return {
          id: idx,
          label: `Date Range : ${formatDateIso(start)} ~ ${formatDateIso(end)}`,
          dates: sorted
        };
      });
  }, [sortedDates, sortBy]);

  // Calculate statistics for a period
  const calculatePeriodStats = (allMeasurements, periodName) => {
    const normalizedPeriod = normalizePeriodName(periodName);
    const periodMeasurements = allMeasurements.filter(m => {
      const ms = getMeasurementEpochMs(m);
      const bundle = resolveTargetsBundleAtMs(ms) || scheduleTargets;
      const periods = Array.isArray(bundle?.weightTimePeriod) ? bundle.weightTimePeriod : [];
      const periodLabel = resolvePeriodNameByIdOrLabel(periods, m) || m.period_name || m.period;
      let resolved = periodLabel;
      if (!resolved) {
        const mTime = m.measurement_time || m.measure_new_date_time?.split('T')[1] || m.measure_date_time?.split(' ')[1];
        if (mTime) resolved = resolvePeriodByTime(mTime, periods);
      }
      if (!resolved) resolved = 'Unknown';
      return normalizePeriodName(resolved) === normalizedPeriod;
    });

    if (!periodMeasurements || periodMeasurements.length === 0) {
      return { 
        avg: { weight: null, fat: null, bmi: null }, 
        max: { weight: null, fat: null, bmi: null }, 
        min: { weight: null, fat: null, bmi: null }, 
        count: 0, 
        above: { weight: 0, fat: 0, bmi: 0 }, 
        below: { weight: 0, fat: 0, bmi: 0 }, 
        within: { weight: 0, fat: 0, bmi: 0 } 
      };
    }

    const weightValues = periodMeasurements
      .map((m) => {
        const w = m.weight;
        if (w == null || w === '') return null;
        const n = parseFloat(w);
        return Number.isFinite(n) ? n : null;
      })
      .filter((v) => v != null);
    
    const fatValues = periodMeasurements.map(m => parseFloat(m.fat || m.body_fat)).filter(v => !isNaN(v));
    const bmiValues = periodMeasurements.map(m => parseFloat(m.bmi)).filter(v => !isNaN(v));

    const weightAvg = weightValues.length > 0 ? weightValues.reduce((a, b) => a + b, 0) / weightValues.length : null;
    const weightMax = weightValues.length > 0 ? Math.max(...weightValues) : null;
    const weightMin = weightValues.length > 0 ? Math.min(...weightValues) : null;
    
    const fatAvg = fatValues.length > 0 ? fatValues.reduce((a, b) => a + b, 0) / fatValues.length : null;
    const fatMax = fatValues.length > 0 ? Math.max(...fatValues) : null;
    const fatMin = fatValues.length > 0 ? Math.min(...fatValues) : null;
    
    const bmiAvg = bmiValues.length > 0 ? bmiValues.reduce((a, b) => a + b, 0) / bmiValues.length : null;
    const bmiMax = bmiValues.length > 0 ? Math.max(...bmiValues) : null;
    const bmiMin = bmiValues.length > 0 ? Math.min(...bmiValues) : null;

    let aboveWeight = 0, belowWeight = 0, withinWeight = 0;
    let aboveFat = 0, belowFat = 0, withinFat = 0;
    let aboveBmi = 0, belowBmi = 0, withinBmi = 0;
    
    periodMeasurements.forEach(m => {
      const bundle = resolveTargetsBundleAtMs(getMeasurementEpochMs(m)) || scheduleTargets;
      const target = getWeightTarget(bundle);
      const weightKg = m.weight != null && m.weight !== '' ? parseFloat(m.weight) : null;
      const fat = m.fat || m.body_fat;
      const bmi = m.bmi;

      if (weightKg != null && !Number.isNaN(weightKg)) {
        const status = checkWeightValue(weightKg, target);
        if (status === 'high') aboveWeight++;
        else if (status === 'low') belowWeight++;
        else withinWeight++;
      }

      if (fat != null) {
        const fatValue = parseFloat(fat);
        const fatMin = target.bodyFatMin || 14;
        const fatMax = target.bodyFatMax || 27;
        if (fatValue > fatMax) aboveFat++;
        else if (fatValue < fatMin) belowFat++;
        else withinFat++;
      }

      if (bmi != null) {
        const bmiValue = parseFloat(bmi);
        const bmiNormal = target.bmiNormal || 18.5;
        const bmiOverweight = target.bmiOverweight || 25;
        const bmiObese = target.bmiObese || 30;
        if (bmiValue >= bmiObese) aboveBmi++;
        else if (bmiValue < bmiNormal) belowBmi++;
        else withinBmi++;
      }
    });

    return {
      avg: { weight: weightAvg, fat: fatAvg, bmi: bmiAvg },
      max: { weight: weightMax, fat: fatMax, bmi: bmiMax },
      min: { weight: weightMin, fat: fatMin, bmi: bmiMin },
      count: periodMeasurements.length,
      above: { weight: aboveWeight, fat: aboveFat, bmi: aboveBmi },
      below: { weight: belowWeight, fat: belowFat, bmi: belowBmi },
      within: { weight: withinWeight, fat: withinFat, bmi: withinBmi }
    };
  };

  // Render all readings for one period/date inside same cells
  const renderPeriodMeasurementsCells = (periodMeasurements, periodName) => {
    if (!periodMeasurements || periodMeasurements.length === 0) {
      return (
        <>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
        </>
      );
    }
    
    const readings = [...periodMeasurements].sort((a, b) => {
      const timeA = a.measurement_time ||
        a.measure_new_date_time?.split('T')[1] ||
        a.measure_date_time?.split(' ')[1] ||
        a.created_at?.split('T')[1] ||
        '00:00:00';
      const timeB = b.measurement_time ||
        b.measure_new_date_time?.split('T')[1] ||
        b.measure_date_time?.split(' ')[1] ||
        b.created_at?.split('T')[1] ||
        '00:00:00';
      return timeB.localeCompare(timeA);
    });

    return (
      <>
        <td>
          <div className="w-cell-stack">
            {readings.map((measurement, index) => {
              const bundle =
                resolveTargetsBundleAtMs(getMeasurementEpochMs(measurement)) || scheduleTargets;
              const target = getWeightTarget(bundle);
              const weightKg =
                measurement.weight != null && measurement.weight !== ''
                  ? parseFloat(measurement.weight)
                  : null;
              const weightStatus =
                weightKg != null && !Number.isNaN(weightKg)
                  ? checkWeightValue(weightKg, target)
                  : 'normal';
              const weightClass = getMeasurementValueClass(weightStatus);
              return (
                <div key={`${measurement.id || index}-weight`} className={weightClass}>
                  {weightKg != null && !Number.isNaN(weightKg) ? weightKg.toFixed(1) : ''}
                </div>
              );
            })}
          </div>
        </td>
        <td>
          <div className="w-cell-stack">
            {readings.map((measurement, index) => {
              const bundle =
                resolveTargetsBundleAtMs(getMeasurementEpochMs(measurement)) || scheduleTargets;
              const target = getWeightTarget(bundle);
              const fat = measurement.fat || measurement.body_fat;
              let fatClass = 'value-normal';
              if (fat != null) {
                const fatValue = parseFloat(fat);
                const fatMin = target.bodyFatMin || 14;
                const fatMax = target.bodyFatMax || 27;
                if (fatValue > fatMax) fatClass = 'value-high';
                else if (fatValue < fatMin) fatClass = 'value-low';
              }
              return (
                <div key={`${measurement.id || index}-fat`} className={fatClass}>
                  {fat ? parseFloat(fat).toFixed(1) : ''}
                </div>
              );
            })}
          </div>
        </td>
        <td>
          <div className="w-cell-stack">
            {readings.map((measurement, index) => {
              const bundle =
                resolveTargetsBundleAtMs(getMeasurementEpochMs(measurement)) || scheduleTargets;
              const target = getWeightTarget(bundle);
              const bmi = measurement.bmi;
              let bmiClass = 'value-normal';
              if (bmi != null) {
                const bmiValue = parseFloat(bmi);
                const bmiNormal = target.bmiNormal || 18.5;
                const bmiObese = target.bmiObese || 30;
                if (bmiValue >= bmiObese) bmiClass = 'value-high';
                else if (bmiValue < bmiNormal) bmiClass = 'value-low';
              }
              return (
                <div key={`${measurement.id || index}-bmi`} className={bmiClass}>
                  {bmi ? parseFloat(bmi).toFixed(1) : ''}
                </div>
              );
            })}
          </div>
        </td>
        <td>
          <div className="w-cell-stack">
            {readings.map((measurement, index) => {
              const measureTime = measurement.measurement_time ||
                measurement.measure_new_date_time?.split('T')[1]?.slice(0, 5) ||
                measurement.measure_date_time?.split(' ')[1]?.slice(0, 5) ||
                '';
              const formattedTime = measureTime ? formatTime(measureTime) : '';
              return <div key={`${measurement.id || index}-time`} className="w-time-text">{formattedTime}</div>;
            })}
          </div>
        </td>
      </>
    );
  };

  const formatPercentCount = (count, total) => {
    if (!total) return `0% (${count})`;
    const pct = Math.round((count / total) * 100);
    return `${pct}% (${count})`;
  };

  const calculateOverallTargetStats = () => {
    const stats = {
      above: { weight: 0, bmi: 0, fat: 0 },
      below: { weight: 0, bmi: 0, fat: 0 },
      within: { weight: 0, bmi: 0, fat: 0 }
    };

    effectiveMeasurements.forEach((m) => {
      const bundle = resolveTargetsBundleAtMs(getMeasurementEpochMs(m)) || scheduleTargets;
      const target = getWeightTarget(bundle);
      const weightKg = m.weight != null ? Number(m.weight) : null;
      if (weightKg != null && !Number.isNaN(weightKg)) {
        const s = checkWeightValue(weightKg, target);
        if (s === 'high') stats.above.weight += 1;
        else if (s === 'low') stats.below.weight += 1;
        else stats.within.weight += 1;
      }

      const fatRaw = m.fat ?? m.body_fat;
      const fatVal = fatRaw != null ? Number(fatRaw) : null;
      if (fatVal != null && !Number.isNaN(fatVal)) {
        const fatMin = Number(target.bodyFatMin || 14);
        const fatMax = Number(target.bodyFatMax || 27);
        if (fatVal > fatMax) stats.above.fat += 1;
        else if (fatVal < fatMin) stats.below.fat += 1;
        else stats.within.fat += 1;
      }

      const bmiVal = m.bmi != null ? Number(m.bmi) : null;
      if (bmiVal != null && !Number.isNaN(bmiVal)) {
        const bmiNormal = Number(target.bmiNormal || 18.5);
        const bmiObese = Number(target.bmiObese || 30);
        if (bmiVal >= bmiObese) stats.above.bmi += 1;
        else if (bmiVal < bmiNormal) stats.below.bmi += 1;
        else stats.within.bmi += 1;
      }
    });

    return stats;
  };

  const renderTargetBlock = (targetKind = 'below') => {
    const stats = calculateOverallTargetStats();
    const weightTotal = (stats.above.weight || 0) + (stats.below.weight || 0) + (stats.within.weight || 0);
    const bmiTotal = (stats.above.bmi || 0) + (stats.below.bmi || 0) + (stats.within.bmi || 0);
    const fatTotal = (stats.above.fat || 0) + (stats.below.fat || 0) + (stats.within.fat || 0);
    const titleMap = { below: 'Below target', above: 'Above target', within: 'Within target' };

    return (
      <div className="w-total-block">
        <div className="w-total-title">{titleMap[targetKind] || 'Below target'}</div>
        <div>Weight : {formatPercentCount(stats[targetKind]?.weight || 0, weightTotal)}</div>
        <div>Bmi : {formatPercentCount(stats[targetKind]?.bmi || 0, bmiTotal)}</div>
        <div>Fat : {formatPercentCount(stats[targetKind]?.fat || 0, fatTotal)}</div>
      </div>
    );
  };

  // Get patient info for display
  const patient = patientData?.patient;
  const patientInfo = useMemo(() => {
    const patientNameLine = patient ? `${patient.last_name || ''}, ${patient.first_name || ''}`.trim() : 'Loading...';
    const patientDOB = patient?.date_of_birth
      ? `${formatDate(patient.date_of_birth)} (${new Date().getFullYear() - new Date(patient.date_of_birth).getFullYear()} years)`
      : 'N/A';
    const patientPhone = patient?.phone_number || 'N/A';
    const patientDevice = patientData?.device_serial || 'N/A';
    return { patientNameLine, patientDOB, patientPhone, patientDevice };
  }, [patient, patientData?.device_serial]);
  const { patientNameLine, patientDOB, patientPhone, patientDevice } = patientInfo;

  // Set breadcrumbs (must be after patientName is declared)
  useEffect(() => {
    if (patientData) {
      const resolvedPracticeName =
        patientData?.patient?.practice_name ||
        patientData?.practice?.practice_name ||
        patientData?.practice_name ||
        '';
      const practiceLabel = String(resolvedPracticeName).trim() || 'Practice';
      updateBreadcrumbs([
        { label: 'Practices', path: '/practices', active: false },
        { label: practiceLabel, path: `/practices/${practiceId}`, active: false },
        { label: 'Patients', path: `/practices/${practiceId}?tab=patient`, active: false },
        { label: patientNameLine, path: `/practices/${practiceId}/patients/${patientId}`, active: false },
        { label: 'Summary Of Measurement', path: null, active: true }
      ]);
    }
  }, [patientData, practiceId, patientId, patientNameLine, updateBreadcrumbs]);

  const reportTitle = `Weight Report - ${patientNameLine}`;
  const exportFilename = `Weight-Report-${patientNameLine.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;

  const handlePrint = () => {
    try {
      const el = document.getElementById('w-report-content');
      printElement(el, reportTitle);
    } catch (e) {
      toast.error(e?.message || 'Print failed');
    }
  };

  const resolvePeriodForMeasurement = (date, periodList) => {
    const dateMeasurements = groupedMeasurements[date] || [];
    const perPeriod = {};

    dateMeasurements.forEach((m) => {
      const ms = getMeasurementEpochMs(m);
      const bundle = resolveTargetsBundleAtMs(ms) || scheduleTargets;
      const periods = Array.isArray(bundle?.weightTimePeriod) ? bundle.weightTimePeriod : [];
      let period =
        resolvePeriodNameByIdOrLabel(periods, m) || m.period_name || m.period || 'Unknown';
      if (!period || period === 'Unknown') {
        const mTime =
          m.measurement_time ||
          m.measure_new_date_time?.split('T')[1] ||
          m.measure_date_time?.split(' ')[1];
        if (mTime) {
          const byTime = resolvePeriodByTime(mTime, periods);
          if (byTime) period = byTime;
        }
      }
      const matchedPeriod = periodList.find(
        (p) => normalizePeriodName(p) === normalizePeriodName(period)
      );
      if (!matchedPeriod) return;
      if (!perPeriod[matchedPeriod]) perPeriod[matchedPeriod] = [];
      perPeriod[matchedPeriod].push(m);
    });

    return perPeriod;
  };

  const handleExport = () => {
    if (effectiveMeasurements.length === 0) {
      toast.warning('No data to export');
      return;
    }
    try {
      exportWeightTablesPdf({
        filename: exportFilename,
        title: reportTitle,
        patient: {
          name: patientNameLine,
          dob: patientDOB,
          phone: patientPhone,
          device: patientDevice
        },
        periodList:
          periodNames.length > 0 ? periodNames : ['Morning', 'Afternoon', 'Evening'],
        dateBuckets,
        groupedMeasurements,
        resolvePeriodForMeasurement,
        calculatePeriodStats,
        effectiveMeasurements,
        calculateOverallTargetStats,
        formatPercentCount,
        formatDate,
        formatTime
      });
      toast.success('Exported to PDF');
    } catch (e) {
      toast.error(e?.message || 'Export failed');
    }
  };

  const defaultEmailSubject = `Weight Report - ${patientNameLine} (${new Date().toLocaleDateString()})`;
  const defaultEmailContent = `Weight Report\nPatient: ${patientNameLine} (DOB: ${patientDOB})\nGenerated: ${new Date().toLocaleString()}\nTotal Readings: ${effectiveMeasurements.length}`;

  return (
    <div className="weight-page">
      {practiceIsLocked && (
        <div className="practice-locked-measurement-banner" role="status">
          This practice is locked. Historical readings remain available for up to 30 days. New measurements and
          follow-ups are not allowed.
        </div>
      )}
      <div id="w-report-content" style={{ padding: '0px' }}>
      <>
      
      <div className="w-layout-grid">
        <h4 className="w-layout-title" data-export-ignore="true">Weight Measurement Controls</h4>
        <div className="w-layout-top-row">
          {!practiceIsLocked && (
            <div className="w-timer-wrapper" data-export-ignore="true">
               <div className="w-timer-box">
                  <span className="w-timer-value">{formatTimer(timer)}</span>
               </div>
               <button
                  type="button"
                  className="w-timer-pause-btn"
                  onClick={() => setIsRunning(prev => !prev)}
                  title={isRunning ? "Pause Timer" : "Start Timer"}
               >
                  {isRunning ? (
                    <div className="w-pause-icon"><span>|</span><span>|</span></div>
                  ) : (
                    <FontAwesomeIcon icon={faPlay} className="w-play-icon" />
                  )}
               </button>
            </div>
          )}

          <div className="w-patient-details-grid">
            <div className="w-info-item">
              <span className="w-label">Name :</span>
              <span className="w-value">{patientNameLine}</span>
            </div>
            <div className="w-info-item">
              <span className="w-label">DOB :</span>
              <span className="w-value">{patientDOB}</span>
            </div>
            <div className="w-info-item">
              <span className="w-label">Phone :</span>
              <span className="w-value">{patientPhone}</span>
            </div>
            <div className="w-info-item">
              <span className="w-label">Device :</span>
              <span className="w-value">{patientDevice}</span>
            </div>
          </div>
        </div>

        <div className="w-layout-middle-row" data-export-ignore="true">
          <div className="w-middle-left">
            <VitalsTabs
              practiceId={practiceId}
              patientId={patientId}
              vitals={patientVitals}
              active="weight"
              mode="measurement"
            />
          </div>
          {!practiceIsLocked && (
            <div className="w-middle-right">
              <button
                className="w-add-followup-btn"
                type="button"
                onClick={() => setShowFollowUpModal(true)}
              >
                Add Follow Up
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Query Section */}
      <div className="w-outline-section w-query-box-custom" data-export-ignore="true">
        <h4 className="w-outline-title w-query-title-custom">Query Period</h4>
        <div className="w-query-body">
          <div className="w-query-left">
            <div className="w-radio-row w-query-row-first">
              <label className="w-radio-label">
                <input
                  type="radio"
                  name="queryType"
                  value="dateRange"
                  checked={queryType === 'dateRange'}
                  onChange={(e) => setQueryType(e.target.value)}
                />
                <span style={{ fontWeight: 600, minWidth: '80px' }}>Date Range</span>
              </label>
              <select
                className="w-dropdown"
                style={{ marginLeft: '20px', flex: 1 }}
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                disabled={queryType !== 'dateRange'}
              >
                <option>Last 30 days</option>
                <option>Last 7 days</option>
                <option>Last 14 days</option>
                <option>Last 60 days</option>
                <option>Last 90 days</option>
              </select>
            </div>
            
            <div className="w-radio-row" style={{ marginTop: '20px' }}>
              <label className="w-radio-label">
                <input
                  type="radio"
                  name="queryType"
                  value="from"
                  checked={queryType === 'from'}
                  onChange={(e) => setQueryType(e.target.value)}
                />
                <span style={{ fontWeight: 600, minWidth: '80px' }}>From</span>
              </label>
              <div className="w-date-inputs" style={{ marginLeft: '20px', flex: 1, display: 'flex', alignItems: 'center' }}>
                <input
                  type="date"
                  className="w-date-input"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  onClick={(e) => e.target.showPicker && e.target.showPicker()}
                  disabled={queryType !== 'from'}
                />
                <span className="w-date-separator" style={{ margin: '0 15px', fontWeight: 600 }}>To</span>
                <input
                  type="date"
                  className="w-date-input"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  onClick={(e) => e.target.showPicker && e.target.showPicker()}
                  disabled={queryType !== 'from'}
                />
              </div>
            </div>
          </div>
          
          <div className="w-query-right" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div className="w-radio-row w-query-row-first">
              <div style={{ fontWeight: 600, minWidth: '85px' }}>Sort Data by</div>
              <select
                className="w-dropdown"
                style={{ marginLeft: '20px', flex: 1 }}
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option>1 Day Average</option>
                <option>Weekly average</option>
                <option>14 Days Average</option>
                <option>Monthly average</option>
                <option>3 Months Average</option>
                <option>All</option>
              </select>
            </div>
            
            <div className="w-action-buttons-row">
              <button className="w-action-btn" onClick={handleQuery}>
                Query
              </button>
              {!practiceIsLocked && (
                <button className="w-action-btn" onClick={handleOpenAddModal}>Add Data</button>
              )}
              <button className="w-action-btn" onClick={handlePrint}>Print</button>
              <button className="w-action-btn" onClick={handleExport}>Export</button>
              <button className="w-action-btn" onClick={() => setShowEmailModal(true)}>Email</button>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Measurement Log Table */}
      <div className="w-outline-section">
        <h4 className="w-outline-title">Weight</h4>
        {queryLoading || awaitingTargetsForColoring ? (
          <PageLoadingSpinner minHeight="280px" />
        ) : (
        <>
        {dateBuckets.length > 0 ? (
          dateBuckets.map((bucket) => {
            const periodList = periodNames.length > 0 ? periodNames : ['Morning', 'Afternoon', 'Evening'];
            const dateMeasurementsByPeriodMap = {};
            bucket.dates.forEach((date) => {
              const dateMeasurements = groupedMeasurements[date] || [];
              const perPeriod = {};
              dateMeasurements.forEach((m) => {
                const ms = getMeasurementEpochMs(m);
                const bundle = resolveTargetsBundleAtMs(ms) || scheduleTargets;
                const periods = Array.isArray(bundle?.weightTimePeriod) ? bundle.weightTimePeriod : [];
                let period =
                  resolvePeriodNameByIdOrLabel(periods, m) || m.period_name || m.period || 'Unknown';
                if (!period || period === 'Unknown') {
                  const mTime =
                    m.measurement_time || m.measure_new_date_time?.split('T')[1] || m.measure_date_time?.split(' ')[1];
                  if (mTime) {
                    const byTime = resolvePeriodByTime(mTime, periods);
                    if (byTime) period = byTime;
                  }
                }
                const matchedPeriod = periodList.find(
                  (p) => normalizePeriodName(p) === normalizePeriodName(period)
                );
                if (!matchedPeriod) return;
                if (!perPeriod[matchedPeriod]) perPeriod[matchedPeriod] = [];
                perPeriod[matchedPeriod].push(m);
              });
              dateMeasurementsByPeriodMap[date] = perPeriod;
            });

            return (
              <div className="w-measurement-table-section w-section-table" key={`bucket-${bucket.id}`}>
                <div className="w-time-text" style={{ padding: '4px 10px', fontWeight: 500, color: '#2c2c2c', fontSize: '12px' }}>{bucket.label}</div>
                <table className="w-measurement-table">
                  <thead>
                    <tr>
                      <th rowSpan="2" className="w-table-label-cell">Date</th>
                      {periodList.map((periodName, periodIndex) => {
                        const periodInfo = getPeriodInfo(periodName);
                        const timeRange = periodInfo && periodInfo.startTimeDisplay && periodInfo.endTimeDisplay
                          ? `${periodInfo.startTimeDisplay} ~ ${periodInfo.endTimeDisplay}`
                          : '';
                        return (
                          <React.Fragment key={`head-${bucket.id}-${periodName}`}>
                            <th colSpan="4" className="w-table-header-cell" style={{ backgroundColor: getPeriodColor(periodIndex) }}>
                              {periodName}
                              {timeRange && (
                                <>
                                  <br />
                                  <span className="w-time-text">{timeRange}</span>
                                </>
                              )}
                            </th>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                    <tr>
                      {periodList.map((periodName) => (
                        <React.Fragment key={`sub-${bucket.id}-${periodName}`}>
                          <th className="w-table-sub-header">Weight</th>
                          <th className="w-table-sub-header">Body Fat</th>
                          <th className="w-table-sub-header">BMI</th>
                          <th className="w-table-sub-header">Measure Time</th>
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {bucket.dates.map((date) => (
                      <tr key={`row-${bucket.id}-${date}`}>
                        <td className="w-table-label-cell">{formatDate(date)}</td>
                        {periodList.map((periodName) => (
                          <React.Fragment key={`cell-${bucket.id}-${date}-${periodName}`}>
                            {renderPeriodMeasurementsCells(dateMeasurementsByPeriodMap[date]?.[periodName] || [], periodName)}
                          </React.Fragment>
                        ))}
                      </tr>
                    ))}

                    <tr className="w-average-row">
                      <td className="w-table-label-cell">Average</td>
                      {periodList.map((periodName) => {
                        const periodMeasurements = [];
                        bucket.dates.forEach((date) => {
                          const rows = dateMeasurementsByPeriodMap[date]?.[periodName] || [];
                          periodMeasurements.push(...rows);
                        });

                        const wVals = periodMeasurements
                          .map((m) => {
                            if (m.weight == null || m.weight === '') return null;
                            const n = parseFloat(m.weight);
                            return Number.isFinite(n) ? n : null;
                          })
                          .filter((v) => v != null);
                        const fVals = periodMeasurements.map(m => parseFloat(m.fat || m.body_fat)).filter(v => !isNaN(v));
                        const bVals = periodMeasurements.map(m => parseFloat(m.bmi)).filter(v => !isNaN(v));

                        return (
                          <React.Fragment key={`avg-${bucket.id}-${periodName}`}>
                            <td style={{ fontWeight: 600 }}>{wVals.length > 0 ? (wVals.reduce((a, b) => a + b, 0) / wVals.length).toFixed(1) : '-'}</td>
                            <td style={{ fontWeight: 600 }}>{fVals.length > 0 ? (fVals.reduce((a, b) => a + b, 0) / fVals.length).toFixed(1) : '-'}</td>
                            <td style={{ fontWeight: 600 }}>{bVals.length > 0 ? (bVals.reduce((a, b) => a + b, 0) / bVals.length).toFixed(1) : '-'}</td>
                            <td></td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            );
          })
        ) : (
          <div className="w-measurement-table-section w-section-table">
            <table className="w-measurement-table">
              <tbody>
                <tr>
                  <td className="w-table-label-cell" style={{ textAlign: 'center', padding: '20px' }}>
                    No measurement data available
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
        </>
        )}
      </div>

      {/* Summary Statistics Table */}
      <div className="w-outline-section w-summary-section">
        <h4 className="w-outline-title">Summary Of Measurement</h4>
        {queryLoading || awaitingTargetsForColoring ? (
          <PageLoadingSpinner minHeight="240px" />
        ) : (
        <div className="w-measurement-table-section w-section-table">
          <table className="w-measurement-table">
          <thead>
            <tr>
              <th rowSpan="2" className="w-table-label-cell">Statistics</th>
              {periodNames.length > 0 ? (
                periodNames.map((periodName, periodIndex) => {
                  const periodInfo = getPeriodInfo(periodName);
                  const timeRange = periodInfo && periodInfo.startTimeDisplay && periodInfo.endTimeDisplay
                    ? `${periodInfo.startTimeDisplay} ~ ${periodInfo.endTimeDisplay}`
                    : '';
                  return (
                    <React.Fragment key={periodName}>
                      <th colSpan="3" className="w-table-header-cell" style={{ backgroundColor: getPeriodColor(periodIndex) }}>
                        {periodName}
                        {timeRange && (
                          <>
                            <br />
                            <span className="w-time-text">{timeRange}</span>
                          </>
                        )}
                      </th>
                    </React.Fragment>
                  );
                })
              ) : (
                <>
                  {['Morning', 'Afternoon', 'Evening'].map(periodName => {
                    const periodInfo = getPeriodInfo(periodName);
                    const timeRange = periodInfo && periodInfo.startTimeDisplay && periodInfo.endTimeDisplay
                      ? `${periodInfo.startTimeDisplay} ~ ${periodInfo.endTimeDisplay}`
                      : '';
                    return (
                      <th key={periodName} colSpan="3" className="w-table-header-cell">
                        {periodName}
                        {timeRange && (
                          <>
                            <br />
                            <span className="w-time-text">{timeRange}</span>
                          </>
                        )}
                      </th>
                    );
                  })}
                </>
              )}
            </tr>
            <tr>
              {(periodNames.length > 0 ? periodNames : ['Morning', 'Afternoon', 'Evening']).map(periodName => (
                <React.Fragment key={periodName}>
                  <th className="w-table-sub-header">Weight</th>
                  <th className="w-table-sub-header">Body Fat</th>
                  <th className="w-table-sub-header">BMI</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {['Average', 'Maximum', 'Minimum', 'Count', 'Above Target', 'Below Target', 'Within Target'].map(statType => (
              <tr key={statType}>
                <td className="w-table-label-cell">{statType}</td>
                {(periodNames.length > 0 ? periodNames : ['Morning', 'Afternoon', 'Evening']).map(periodName => {
                  const stats = calculatePeriodStats(effectiveMeasurements, periodName);
                  
                  if (statType === 'Average') {
                    return (
                      <React.Fragment key={periodName}>
                        <td>{stats.avg.weight ? parseFloat(stats.avg.weight).toFixed(1) : ''}</td>
                        <td>{stats.avg.fat ? parseFloat(stats.avg.fat).toFixed(1) : ''}</td>
                        <td>{stats.avg.bmi ? parseFloat(stats.avg.bmi).toFixed(1) : ''}</td>
                      </React.Fragment>
                    );
                  } else if (statType === 'Maximum') {
                    return (
                      <React.Fragment key={periodName}>
                        <td>{stats.max.weight ? parseFloat(stats.max.weight).toFixed(1) : ''}</td>
                        <td>{stats.max.fat ? parseFloat(stats.max.fat).toFixed(1) : ''}</td>
                        <td>{stats.max.bmi ? parseFloat(stats.max.bmi).toFixed(1) : ''}</td>
                      </React.Fragment>
                    );
                  } else if (statType === 'Minimum') {
                    return (
                      <React.Fragment key={periodName}>
                        <td>{stats.min.weight ? parseFloat(stats.min.weight).toFixed(1) : ''}</td>
                        <td>{stats.min.fat ? parseFloat(stats.min.fat).toFixed(1) : ''}</td>
                        <td>{stats.min.bmi ? parseFloat(stats.min.bmi).toFixed(1) : ''}</td>
                      </React.Fragment>
                    );
                  } else if (statType === 'Count') {
                    return (
                      <React.Fragment key={periodName}>
                        <td>{stats.count}</td>
                        <td>{stats.count}</td>
                        <td>{stats.count}</td>
                      </React.Fragment>
                    );
                  } else if (statType === 'Above Target') {
                    return (
                      <React.Fragment key={periodName}>
                        <td>{stats.above.weight}</td>
                        <td>{stats.above.fat}</td>
                        <td>{stats.above.bmi}</td>
                      </React.Fragment>
                    );
                  } else if (statType === 'Below Target') {
                    return (
                      <React.Fragment key={periodName}>
                        <td>{stats.below.weight}</td>
                        <td>{stats.below.fat}</td>
                        <td>{stats.below.bmi}</td>
                      </React.Fragment>
                    );
                  } else if (statType === 'Within Target') {
                    return (
                      <React.Fragment key={periodName}>
                        <td>{stats.within.weight}</td>
                        <td>{stats.within.fat}</td>
                        <td>{stats.within.bmi}</td>
                      </React.Fragment>
                    );
                  }
                  return (
                    <React.Fragment key={periodName}>
                      <td></td>
                      <td></td>
                      <td></td>
                    </React.Fragment>
                  );
                })}
              </tr>
            ))}
            <tr>
              <td className="w-table-label-cell" style={{ backgroundColor: '#d3d3d3' }}>Total</td>
              <td colSpan="3" className="w-table-label-cell" style={{ backgroundColor: '#d3d3d3' }}>
                {renderTargetBlock('below')}
              </td>
              <td colSpan="3" className="w-table-label-cell" style={{ backgroundColor: '#d3d3d3' }}>
                {renderTargetBlock('above')}
              </td>
              <td colSpan="3" className="w-table-label-cell" style={{ backgroundColor: '#d3d3d3' }}>
                {renderTargetBlock('within')}
              </td>
            </tr>
          </tbody>
          </table>
        </div>
        )}
      </div>

      {/* Add Weight Modal */}
      {!practiceIsLocked && showAddModal && (
        <AddWForm
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          practiceId={practiceId}
          patientId={patientId}
          initialScheduleTargets={scheduleTargets}
          onSuccess={handleAddDataSuccess}
        />
      )}

      {!practiceIsLocked && showFollowUpModal && (
        <AddFollowUpModal
          isOpen={showFollowUpModal}
          onClose={() => setShowFollowUpModal(false)}
          practiceId={practiceId}
          patientId={patientId}
          patientName={patientNameLine}
          initialTimerValue={timer}
          onSuccess={() => {}}
        />
      )}
      </>
      </div>{/* end w-report-content */}

      <EmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        defaultSubject={defaultEmailSubject}
        defaultContent={defaultEmailContent}
        onSend={async (emailData) => {
          try {
            if (emailData.attachments?.length) {
              toast.info('Attachments are not supported yet. Sending without attachments.');
            }
            setEmailSending(true);
            const payload = {
              to: emailData.to,
              cc: emailData.cc,
              bcc: emailData.bcc,
              subject: emailData.subject,
              text: emailData.content,
              html: `<pre style="font-family: Arial, sans-serif; white-space: pre-wrap;">${String(emailData.content || '').replace(/</g, '&lt;')}</pre>`,
              event_id: 11
            };
            const res = await emailService.sendCustomNotification(payload);
            if (!res.success) throw new Error(res.error || res.message);
            toast.success('Email queued successfully');
            setShowEmailModal(false);
          } catch (e) {
            toast.error(e?.message || 'Failed to send email');
          } finally {
            setEmailSending(false);
          }
        }}
      />
    </div>
  );
};

export default Weight;

